import { inject, Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { environment } from '../../environments/environment';

import { AuthService } from './auth.service';
import { BehaviorSubject } from 'rxjs';
import { interval } from '../utils/interval';

type SocketEntityName = 'broker_device';

type SocketPayload<T> = {
  action: 'subscribe' | 'unsubscribe' | 'ping' | 'pong' | 'data';
  content?: T;
  token?: string;
  fields?: Record<SocketEntityName, string[] | '*'>; // Key is Kafka topic, value is an array of table column in database.
};

type ConnectionArgs<T> = {
  entity: SocketEntityName;
  fields: string[] | '*';
};

export type WSState = 'connected' | 'disconnected' | 'reconnecting' | 'failed';

export interface WebSocketStatus {
  state: WSState;
}

export const RECONNECT_INTERVAL = environment.reconnectInterval;

@Injectable({
  providedIn: 'root',
})
export class SocketService<T> {
  private readonly _MAX_RETRIES = 3;
  private readonly _RETRY_LONG_DELAY = 2 * 60 * 1000; // ms
  private readonly _PING_INTERVAL = 5000; // ms
  private readonly _PONG_TIMEOUT = 10000; // ms

  private _authService = inject(AuthService);
  private _workerService: Worker = new Worker(new URL('./ping.worker.ts', import.meta.url));
  private _lastPongTime = Date.now();
  private _longRetryInterval: ReturnType<typeof setInterval>;
  private _retryCounter = 0;
  private _connectionArgs: ConnectionArgs<T>;

  public socket: WebSocketSubject<SocketPayload<T>>;

  public status$ = new BehaviorSubject<WebSocketStatus>({ state: 'disconnected' });

  private createSocket() {
    this.socket = webSocket({
      url: environment.wsEndpoint,
      openObserver: { next: () => this._onOpen() },
      closeObserver: {
        next: event => this._onClose(event),
      },
    });
  }

  public clear() {
    this._stopPing();
  }

  public connect(args?: ConnectionArgs<T>) {
    this.createSocket();
    this.socket.subscribe();

    if (!this._connectionArgs) this._connectionArgs = args;
  }

  public disconnect() {
    if (this.socket) {
      this.socket.next({ action: 'unsubscribe' });
      this.socket.complete();
      this.socket.unsubscribe();

      this.socket = null;
    }
  }

  private _reset() {
    interval.clear(this._longRetryInterval);
    this._longRetryInterval = undefined;
    this._retryCounter = 0;
    this._lastPongTime = Date.now();
  }

  private _onOpen() {
    console.debug('[Websocket] - ✅ connected.');

    this._reset();

    this.socket.subscribe({
      next: value => {
        if (value.action === 'pong') this._lastPongTime = Date.now();
      },
    });

    this.socket.next({
      action: 'subscribe',
      token: this._authService.$token(),
      fields: { [this._connectionArgs.entity]: this._connectionArgs.fields },
    });

    this.status$.next({ state: 'connected' });

    this._startPing();
  }

  private _onClose(event: CloseEvent) {
    console.debug('[Websocket] - ⚠️ closed');

    this.disconnect();

    if (event.code === 1006 || !event.wasClean) {
      console.debug('[Websocket] - 🚫 Abnormal Closure');
      this._retryStrategy();
    }
  }

  private _stopPing() {
    this._workerService.postMessage({ type: 'stop' });
  }

  private _startPing() {
    if (typeof Worker !== 'undefined') {
      this._workerService.postMessage({ type: 'start', pingInterval: this._PING_INTERVAL });

      this._workerService.onmessage = ({ data }) => {
        if (this.socket && data.type === 'ping') {
          const now = Date.now();
          const timeSinceLastPong = now - this._lastPongTime;

          this.socket.next({ action: 'ping' });

          if (timeSinceLastPong > this._PING_INTERVAL + this._PONG_TIMEOUT) {
            console.debug('[Websocket] - 🚫 No pong, start the retry strategy');

            this._stopPing();
            this.socket.complete();
            return;
          }
        }
      };
    }
  }

  private _retryStrategy() {
    // prevent to re create an second long interval
    if (this._longRetryInterval) return;

    if (this._retryCounter >= this._MAX_RETRIES) {
      console.debug(`[Websocket] - 🔄 Max retries has been reached, now retry connection every 2 minutes.`);

      this._longRetryInterval = interval.set(() => {
        this.connect();
      }, this._RETRY_LONG_DELAY);

      return;
    }

    this._stopPing();

    if (this.status$.value.state !== 'reconnecting') this.status$.next({ state: 'reconnecting' });

    this._retryCounter += 1;

    console.debug(`[Websocket] - 🔄 Retry connection ${this._retryCounter}/${this._MAX_RETRIES}...`);

    this.connect();
  }
}
